import { appConfigFileGlob } from '@ms-cloudpack/config';
import { PackageDefinitions } from '@ms-cloudpack/package-utilities';
import { globSync } from 'tinyglobby';
import path from 'path';
import prompts from 'prompts';
import type { Choice } from 'prompts';
import type { AppPathDiscoveryMode } from '../types/AppPathDiscoveryMode.js';
import type { CommandExitParams } from '../types/CommandAction.js';

export type _AppPromptQuestionName = 'app';

/**
 * Gets the appPath from which Cloudpack will run. If the CWD is a Cloudpack-enabled app,
 * we'll use that; otherwise, we'll scan for Cloudpack-enabled app and present the user
 * with a menu. If no apps are found, Cloudpack will just try to run in the CWD anyway.
 *
 * @returns App path(s) (just one unless `discover: 'multi'`), or an exit options object if
 * there was an error.
 */
export async function discoverLocalAppPaths(params: {
  /** Working directory to start from. */
  cwd: string;

  /**
   * How to determine the app path(s). (`--app` in the docs refers to `desiredApp` here.)
   * @default 'single'
   */
  discover?: Exclude<AppPathDiscoveryMode, 'remote'>;

  /**
   * Desired app name or substring, from the `--app` option.
   * - If there's an exact match, only that app will be returned.
   * - If multiple apps match a substring, see `discover` for behavior.
   * - If no match, it's an error.
   */
  desiredApp?: string;
}): Promise<string[] | CommandExitParams> {
  const { cwd, desiredApp, discover } = params;
  const multiApp = discover === 'multi';

  if (!desiredApp && globSync(`./${appConfigFileGlob}`, { cwd })?.length) {
    // If no specific app was requested, and the cwd is a cloudpack app, just use that.
    return [cwd];
  }

  // Get all cloudpack app paths based on the presence of cloudpack.config.json
  const cloudpackAppPaths = (globSync(`**/${appConfigFileGlob}`, { cwd, ignore: ['**/node_modules/**'] }) ?? []).map(
    (file) => path.join(cwd, path.dirname(file)),
  );

  if (!cloudpackAppPaths.length) {
    // Include the cwd if there are no configs found, since it's possible to run start or init
    // without a config.
    cloudpackAppPaths.push(cwd);
  }

  if (!desiredApp && (multiApp || cloudpackAppPaths.length === 1)) {
    // Return all apps found if we're in multi-app mode or only one app was found
    // (and no specific app was requested).
    return cloudpackAppPaths;
  }

  // Get each app's name and description, filtered by `desiredApp` if specified.
  // (These use Choice format in case they're later used in a prompt.)
  const packages = new PackageDefinitions();
  const appChoices = (
    await Promise.all<Omit<Choice, 'value'> & { value: string }>(
      cloudpackAppPaths.map(async (dir) => {
        const pkg = await packages.get(dir);
        return {
          title: pkg.name,
          description: pkg?.description ?? 'An awesome app that would be even more awesome with a proper description',
          value: dir,
        };
      }),
    )
  )
    .filter((choice) => !desiredApp || choice.title.includes(desiredApp))
    .sort((c1, c2) => c1.title.localeCompare(c2.title));

  // Figure out if there are any matches for `desiredApp`
  if (desiredApp) {
    if (!appChoices.length) {
      return {
        message: `No Cloudpack apps found matching the name "${desiredApp}".`,
        hasErrors: true,
      };
    }

    // Prefer an exact match
    const exactMatch = appChoices.find((choice) => choice.title === desiredApp);
    if (exactMatch) {
      return [exactMatch.value];
    }

    // If there's no exact match, it means at least one substring match was found
    if (multiApp || appChoices.length === 1) {
      // Return them all if we're in multi-app mode or only one match was found
      return appChoices.map((choice) => choice.value);
    }
  }

  // There are multiple candidates, so prompt the user to choose one
  // (implicitly, we're in single-app mode if we reach here)
  const response = await prompts<_AppPromptQuestionName>({
    type: 'select',
    name: 'app',
    message: 'Select the app you want to start',
    choices: appChoices,
  });

  // This will be undefined if the user cancels the prompt
  if (!response.app) {
    return { isInterrupted: true, message: '' };
  }

  return [response.app as string];
}
